import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { MailLink } from "@hex-insights/router";
import { Icon, IconContainer } from "../../../../Components/Icon";
import { EmailAddress, Person, PersonNameFragment } from "../../../../Utilities";
import { EmailAddressDeleteButton } from "../../../DeleteButtons";
import { EmailAddressFormat } from "../../../Format";
import { EmailAddressPersonForm } from "../../../Forms";
import { EmailAddressDeletePermissionRequired, EmailAddressUpdatePermissionRequired } from "../PermissionRequired";
import { EmailAddressViewModal } from "../ViewModal";
import styles from "./styles.module.css";

export type CardEmailAddress = Pick<EmailAddress, "id" | "name" | "emailAddress" | "isPrimary">;

export type EditableCardEmailAddress = Pick<EmailAddress, "id" | "name" | "emailAddress" | "isPrimary"> & {
	person: Pick<Person, "id"> & PersonNameFragment;
};

export type EmailAddressCardProps = (
	| {
			emailAddress: CardEmailAddress;
			editable?: false;
	  }
	| {
			emailAddress: EditableCardEmailAddress;
			editable: true;
	  }
) & {
	slim?: boolean;
	noLabel?: boolean;
	noUtilities?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function EmailAddressCard({
	emailAddress,
	editable,
	slim = false,
	noLabel = false,
	noUtilities = false,
	className,
	style,
}: EmailAddressCardProps) {
	const { isModalOpen: isViewModalOpen, openModal: openViewModal, closeModal: closeViewModal } = Modal.useToggle();

	const { isModalOpen: isEditModalOpen, openModal: openEditModal, closeModal: closeEditModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Column
				justify="spaced-start"
				verticalSpacing="0.375rem"
				className={makeClassName(
					styles["email-address-card"],
					slim ? styles["email-address-card--slim"] : "",
					className,
				)}
				style={style}
			>
				<If condition={!noLabel}>
					<Conditional>
						<If condition={slim}>
							<Row justify="spaced-start" align="center">
								<TruncatedText className={styles["email-address-card__name"]}>
									{EmailAddressFormat.Fields.name(emailAddress.name) || "Email"}
								</TruncatedText>

								<If condition={emailAddress.isPrimary}>
									<span title="Primary">
										<Icon.Star color="var(--theme-colors__yellow)" size={16} weight="fill" className="block" />
									</span>
								</If>
							</Row>
						</If>
						<Else>
							<Row justify="spaced-start" align="center">
								<IconContainer color="var(--theme-colors__blue--light)">
									<Icon.EnvelopeSimple color="var(--theme-colors__blue)" size={18} />
								</IconContainer>

								<TruncatedText className={styles["email-address-card__name"]}>
									{EmailAddressFormat.name(emailAddress)}
								</TruncatedText>

								<If condition={emailAddress.isPrimary}>
									<span title="Primary">
										<Icon.Star color="var(--theme-colors__yellow)" size={18} weight="fill" className="block" />
									</span>
								</If>
							</Row>
						</Else>
					</Conditional>
				</If>

				<Row justify="space-between" align="center">
					<TruncatedText className={styles["email-address-card__email-address"]}>
						{EmailAddressFormat.Fields.emailAddress(emailAddress.emailAddress)}
					</TruncatedText>

					<Row justify="spaced-start">
						<If condition={!noUtilities}>
							<MailLink email={emailAddress.emailAddress} className="link--no-text-decoration">
								<Icon.PaperPlaneTilt color="var(--primary-color)" size={16} className="block" />
							</MailLink>
							<Button onClick={openViewModal} style={{ padding: 0, border: "none", cursor: "pointer" }}>
								<Icon.Eye color="var(--primary-color)" size={16} className="block" />
							</Button>
						</If>
						<If condition={editable ?? false}>
							<EmailAddressUpdatePermissionRequired>
								<Button onClick={openEditModal} style={{ padding: 0, border: "none", cursor: "pointer" }}>
									<Icon.PencilSimpleLine color="var(--primary-color)" size={16} className="block" />
								</Button>
							</EmailAddressUpdatePermissionRequired>
						</If>
					</Row>
				</Row>
			</Column>

			<Modal.If condition={isViewModalOpen}>
				<EmailAddressViewModal emailAddress={emailAddress} onClose={closeViewModal} />
			</Modal.If>

			{editable && (
				<Modal.If condition={isEditModalOpen}>
					<EditModal emailAddress={emailAddress} onClose={closeEditModal} />
				</Modal.If>
			)}
		</React.Fragment>
	);
}

const editFormStateArgs = { editMode: EditMode.WriteOnly };

type EditModalProps = {
	emailAddress: EditableCardEmailAddress;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function EditModal({ emailAddress, ifRef, onClose, className, style }: EditModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["edit-modal"], className)} style={style}>
			<Modal.Header>
				<Heading.H2 noMargin>Edit Email Address</Heading.H2>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start">
					<EmailAddressPersonForm.ControlledDetail
						emailAddress={emailAddress}
						formStateArgs={editFormStateArgs}
						noChangesDialog
						onSuccess={onSuccess}
					/>

					<EmailAddressDeletePermissionRequired>
						<EmailAddressDeleteButton instanceID={emailAddress.id} size="small" onSuccess={onSuccess} />
					</EmailAddressDeletePermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
